import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Phone, Sparkle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { MaskedLines, FadeUp, Stagger, staggerItem } from "../components/Reveal";
import { api, formatApiErrorDetail } from "../lib/api";

const HERO_TEXTURE =
    "https://images.unsplash.com/photo-1586020435958-8298e2cb5f64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxsdXh1cmlvdXMlMjBibGFjayUyMGFuZCUyMHdoaXRlJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODQ2ODY2Njd8MA&ixlib=rb-4.1.0&q=85";
const HERO_MARBLE =
    "https://images.unsplash.com/photo-1753074070993-50b8f7f7526d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxtYXJibGUlMjBzdGF0dWUlMjBmaW5hbmNlJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzg0Njg2NjY3fDA&ixlib=rb-4.1.0&q=85";
const PORTRAIT =
    "https://images.pexels.com/photos/5292257/pexels-photo-5292257.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FACADE =
    "https://images.pexels.com/photos/14560244/pexels-photo-14560244.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CHAPTERS = [
    {
        n: "01",
        title: "Credit Report Audit",
        body: "A forensic line-by-line review of your Experian, Equifax and TransUnion reports to identify every inaccurate, obsolete, or unverifiable item.",
    },
    {
        n: "02",
        title: "Dispute Filing",
        body: "Custom-drafted disputes filed under FCRA §611 and §623. We escalate directly with bureaus and furnishers — not templates.",
    },
    {
        n: "03",
        title: "Score Improvement",
        body: "Strategic utilization guidance, tradeline optimization and payment sequencing engineered to move your FICO fast and durably.",
    },
    {
        n: "04",
        title: "Debt Negotiation",
        body: "We negotiate pay-for-delete settlements and reduced balances with creditors so removal, not payoff, becomes the outcome.",
    },
    {
        n: "05",
        title: "Collections Removal",
        body: "Aggressive challenges against third-party collectors under FDCPA — validation demands, cease communications, and removal leverage.",
    },
    {
        n: "06",
        title: "Identity Theft Resolution",
        body: "Full identity restoration protocol: FTC filings, bureau blocks, fraud alerts and coordinated removal of fraudulent tradelines.",
    },
];

const PROCESS = [
    { n: "I", label: "Consult", copy: "Free 20-minute strategy call. We pull your reports together." },
    { n: "II", label: "Audit", copy: "48-hour forensic audit. You receive a written action plan." },
    { n: "III", label: "Dispute", copy: "First round of disputes filed within 5 business days." },
    { n: "IV", label: "Track", copy: "Live client portal. Every letter, every response, every score change." },
    { n: "V", label: "Rebuild", copy: "Positive credit rebuild plan. Graduation typically in 4–7 months." },
];

const TESTIMONIALS = [
    {
        q: "Access moved my score 178 points in six months. I closed on a house I was told I'd never qualify for.",
        who: "M. Alvarez",
        role: "Homebuyer, Austin",
    },
    {
        q: "They removed four collections that had haunted me for years. Professional, fast, and quietly relentless.",
        who: "D. Whitaker",
        role: "Small business owner",
    },
    {
        q: "I finally understood what was on my reports. The portal alone is worth the price.",
        who: "K. Nguyen",
        role: "Client since '24",
    },
];

function HeroParallax() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y1 = useTransform(scrollYProgress, [0, 1], [0, -140]);
    const y2 = useTransform(scrollYProgress, [0, 1], [0, 80]);
    const rot = useTransform(scrollYProgress, [0, 1], [0, 8]);

    return (
        <section
            ref={ref}
            className="relative min-h-[100vh] pt-28 md:pt-36 pb-24 md:pb-32 overflow-hidden bg-background grain"
            data-testid="hero-section"
        >
            <div className="mx-auto max-w-[1600px] px-6 md:px-10">
                <div className="grid grid-cols-12 gap-6 items-start">
                    <div className="col-span-12 md:col-span-8">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6 }}
                            className="flex items-center gap-3 mb-8 text-[11px] tracking-[0.24em] uppercase text-muted-foreground"
                        >
                            <span className="w-8 h-px bg-muted-foreground" />
                            <Sparkle size={12} strokeWidth={1.5} />
                            <span>Est. Austin, Texas · A boutique credit studio</span>
                        </motion.div>

                        <MaskedLines
                            as="h1"
                            className="font-editorial tracking-tighter text-[14vw] md:text-[10.5vw] lg:text-[9.4vw] leading-[0.86]"
                            lines={[
                                "Your credit,",
                                <>
                                    <span className="italic text-accent">rebuilt</span> with
                                </>,
                                "quiet precision.",
                            ]}
                            stagger={0.09}
                        />

                        <FadeUp delay={0.9} className="mt-10 grid grid-cols-12 gap-6 items-end">
                            <p className="col-span-12 md:col-span-6 text-base md:text-lg text-muted-foreground max-w-md leading-relaxed">
                                We remove the errors, negotiate what shouldn&rsquo;t be there, and hand
                                you the score you were supposed to have all along.
                            </p>
                            <div className="col-span-12 md:col-span-6 flex flex-wrap items-center gap-3 md:justify-end">
                                <Link
                                    to="/enroll"
                                    data-testid="hero-cta-consult"
                                    className="group inline-flex items-center gap-3 bg-foreground text-background px-6 py-4 text-[13px] tracking-[0.16em] uppercase hover:bg-accent"
                                >
                                    Get Started · $500
                                    <ArrowUpRight
                                        size={16}
                                        className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                    />
                                </Link>
                                <Link
                                    to="/simulator"
                                    data-testid="hero-cta-simulator"
                                    className="group inline-flex items-center gap-3 border border-foreground px-6 py-4 text-[13px] tracking-[0.16em] uppercase hover:bg-foreground hover:text-background"
                                >
                                    Try the score simulator
                                    <ArrowUpRight
                                        size={16}
                                        className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                    />
                                </Link>
                                <a
                                    href="tel:5127831895"
                                    data-testid="hero-cta-call"
                                    className="inline-flex items-center gap-3 border border-foreground px-6 py-4 text-[13px] tracking-[0.16em] uppercase hover:bg-foreground hover:text-background"
                                >
                                    <Phone size={14} />
                                    512.783.1895
                                </a>
                            </div>
                        </FadeUp>
                    </div>

                    <motion.div
                        style={{ y: y1, rotate: rot }}
                        className="hidden md:block col-span-4 relative aspect-[3/4] border hairline overflow-hidden bg-secondary"
                        data-testid="hero-image"
                    >
                        <img
                            src={HERO_MARBLE}
                            alt="Editorial marble sculpture"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 border-[6px] border-background/0 mix-blend-multiply" />
                        <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-[10px] tracking-[0.24em] uppercase text-background">
                            <span className="bg-foreground/80 px-2 py-1 backdrop-blur">Fig. 01</span>
                            <span className="bg-foreground/80 px-2 py-1 backdrop-blur">Fica · 782</span>
                        </div>
                    </motion.div>
                </div>

                <motion.div style={{ y: y2 }} className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[
                        { k: "+178", v: "avg. point lift" },
                        { k: "5 days", v: "first dispute filed" },
                        { k: "94%", v: "removal success" },
                        { k: "2,300+", v: "items removed since '22" },
                    ].map((s, i) => (
                        <FadeUp key={s.k} delay={0.4 + i * 0.06}>
                            <div className="border-t hairline pt-4">
                                <div className="font-editorial text-4xl md:text-5xl tracking-tighter">
                                    {s.k}
                                </div>
                                <div className="mt-1 text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
                                    {s.v}
                                </div>
                            </div>
                        </FadeUp>
                    ))}
                </motion.div>
            </div>

            <div
                className="pointer-events-none absolute -bottom-24 -right-24 w-[480px] h-[480px] opacity-[0.06]"
                style={{
                    backgroundImage: `url(${HERO_TEXTURE})`,
                    backgroundSize: "cover",
                    filter: "grayscale(1)",
                }}
                aria-hidden
            />
        </section>
    );
}

function EditorialMarquee() {
    return (
        <section className="border-y hairline bg-background py-6 md:py-8 overflow-hidden" data-testid="marquee">
            <Marquee gradient={false} speed={38}>
                {Array.from({ length: 2 }).map((_, k) => (
                    <span key={k} className="marquee-item font-editorial text-6xl md:text-8xl tracking-tighter italic">
                        <span>Dispute</span>
                        <span className="not-italic text-accent">·</span>
                        <span>Rebuild</span>
                        <span className="not-italic text-accent">·</span>
                        <span>Remove</span>
                        <span className="not-italic text-accent">·</span>
                        <span>Restore</span>
                        <span className="not-italic text-accent">·</span>
                        <span>Rise</span>
                        <span className="not-italic text-accent">·</span>
                    </span>
                ))}
            </Marquee>
        </section>
    );
}

function ChaptersSection() {
    return (
        <section id="services" className="bg-background text-foreground py-24 md:py-40" data-testid="services-section">
            <div className="mx-auto max-w-[1600px] px-6 md:px-10">
                <div className="grid grid-cols-12 gap-6 items-end">
                    <div className="col-span-12 md:col-span-5">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-6 flex items-center gap-3">
                            <span className="w-8 h-px bg-muted-foreground" />
                            Chapters · Services
                        </div>
                        <h2 className="font-editorial text-6xl md:text-8xl tracking-tighter leading-[0.9]">
                            A six-part <span className="italic text-accent">manifesto</span> for your file.
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-6 md:col-start-7 text-muted-foreground max-w-md text-base leading-relaxed">
                        Each chapter is a discipline. We move through them methodically, with your
                        consent at every step. No boilerplate. No overpromises. Just federal law,
                        applied with craft.
                    </div>
                </div>

                <Stagger className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">
                    {CHAPTERS.map((c) => (
                        <motion.div
                            key={c.n}
                            variants={staggerItem}
                            className="group border-t hairline py-10 md:py-14 grid grid-cols-12 gap-6 items-start hover:bg-secondary/60 transition-colors"
                            data-testid={`chapter-${c.n}`}
                        >
                            <div className="col-span-2 font-mono-ui text-xs tracking-[0.2em] text-muted-foreground pt-1">
                                {c.n}
                            </div>
                            <div className="col-span-10">
                                <h3 className="font-editorial text-3xl md:text-4xl tracking-tight leading-tight">
                                    {c.title}
                                </h3>
                                <p className="mt-4 text-sm md:text-base text-muted-foreground max-w-lg leading-relaxed">
                                    {c.body}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </Stagger>
            </div>
        </section>
    );
}

function ProcessSection() {
    return (
        <section id="process" className="bg-foreground text-background py-24 md:py-40 relative overflow-hidden" data-testid="process-section">
            <div className="mx-auto max-w-[1600px] px-6 md:px-10">
                <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-12 md:col-span-4">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-background/60 mb-6 flex items-center gap-3">
                            <span className="w-8 h-px bg-background/60" />
                            The Process
                        </div>
                        <h2 className="font-editorial text-5xl md:text-7xl tracking-tighter leading-[0.9]">
                            Five acts.
                            <br />
                            <span className="italic">One outcome.</span>
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-7 md:col-start-6">
                        <div className="border-t border-background/20">
                            {PROCESS.map((p) => (
                                <FadeUp key={p.n}>
                                    <div className="grid grid-cols-12 gap-6 items-baseline border-b border-background/20 py-8 md:py-10">
                                        <div className="col-span-2 font-editorial text-3xl italic">{p.n}</div>
                                        <div className="col-span-4">
                                            <div className="uppercase tracking-[0.18em] text-sm">{p.label}</div>
                                        </div>
                                        <div className="col-span-6 text-background/70 text-sm leading-relaxed">
                                            {p.copy}
                                        </div>
                                    </div>
                                </FadeUp>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ResultsSection() {
    return (
        <section id="results" className="bg-background text-foreground py-24 md:py-40" data-testid="results-section">
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-5">
                    <div className="text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-6 flex items-center gap-3">
                        <span className="w-8 h-px bg-muted-foreground" />
                        Client Voices
                    </div>
                    <h2 className="font-editorial text-5xl md:text-7xl tracking-tighter leading-[0.9]">
                        The <span className="italic text-accent">quiet</span> proof.
                    </h2>

                    <div className="mt-10 relative aspect-[4/5] border hairline overflow-hidden max-w-md">
                        <img src={PORTRAIT} alt="Client portrait" className="w-full h-full object-cover" />
                        <div className="absolute inset-3 border border-background/60 pointer-events-none" />
                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-[10px] tracking-[0.22em] uppercase text-background">
                            <span className="bg-foreground/70 px-2 py-1 backdrop-blur">Fig. 02</span>
                            <span className="bg-foreground/70 px-2 py-1 backdrop-blur">Client · Verified</span>
                        </div>
                    </div>
                </div>

                <div className="col-span-12 md:col-span-6 md:col-start-7 space-y-10 md:space-y-16">
                    {TESTIMONIALS.map((t, i) => (
                        <FadeUp key={i} delay={i * 0.08}>
                            <div className="border-t hairline pt-8">
                                <div className="font-mono-ui text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-4">
                                    {String(i + 1).padStart(2, "0")} / {String(TESTIMONIALS.length).padStart(2, "0")}
                                </div>
                                <blockquote className="font-editorial text-3xl md:text-4xl tracking-tight leading-tight">
                                    “{t.q}”
                                </blockquote>
                                <div className="mt-6 text-sm">
                                    <div className="uppercase tracking-[0.16em]">{t.who}</div>
                                    <div className="text-muted-foreground">{t.role}</div>
                                </div>
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ConsultSection() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", goal: "", message: "" });
    const [loading, setLoading] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/consultations", form);
            toast.success("We'll be in touch within one business day.");
            setForm({ name: "", email: "", phone: "", goal: "", message: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="consult" className="bg-secondary text-foreground py-24 md:py-40 relative overflow-hidden" data-testid="consult-section">
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-5">
                    <div className="text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-6 flex items-center gap-3">
                        <span className="w-8 h-px bg-muted-foreground" />
                        Book a consultation
                    </div>
                    <h2 className="font-editorial text-5xl md:text-7xl tracking-tighter leading-[0.9]">
                        Twenty minutes.
                        <br />
                        <span className="italic text-accent">No card required.</span>
                    </h2>
                    <p className="mt-8 text-muted-foreground max-w-md text-base leading-relaxed">
                        Tell us where you are. We&rsquo;ll tell you honestly whether we can help — and if
                        we can, exactly what the next 90 days look like.
                    </p>

                    <div className="mt-12 relative aspect-[16/10] border hairline overflow-hidden max-w-md">
                        <img src={FACADE} alt="Editorial architecture" className="w-full h-full object-cover grayscale" />
                        <div className="absolute inset-2 border border-background/40 pointer-events-none" />
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    className="col-span-12 md:col-span-6 md:col-start-7 bg-background border hairline p-8 md:p-12"
                    data-testid="consult-form"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Field label="Name" name="name" value={form.name} onChange={onChange} required testId="consult-name" />
                        <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testId="consult-email" />
                        <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testId="consult-phone" />
                        <Field label="Goal (e.g. buy a home)" name="goal" value={form.goal} onChange={onChange} testId="consult-goal" />
                    </div>
                    <div className="mt-6">
                        <label className="block text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-3">
                            What&rsquo;s on your mind?
                        </label>
                        <textarea
                            name="message"
                            value={form.message}
                            onChange={onChange}
                            rows={4}
                            data-testid="consult-message"
                            className="w-full bg-transparent border-b hairline focus:border-foreground outline-none py-3 font-mono-ui text-sm"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="consult-submit"
                        className="mt-10 group inline-flex items-center gap-3 bg-foreground text-background px-6 py-4 text-[13px] tracking-[0.16em] uppercase hover:bg-accent disabled:opacity-60"
                    >
                        {loading ? "Sending…" : "Request consultation"}
                        <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </button>
                    <p className="mt-6 font-mono-ui text-xs text-muted-foreground">
                        Or call directly · <a href="tel:5127831895" className="underline underline-offset-4">512.783.1895</a>
                        <br />
                        Email us · <a href="mailto:accesscreditrepair@yahoo.com" className="underline underline-offset-4">accesscreditrepair@yahoo.com</a>
                    </p>
                </form>
            </div>
        </section>
    );
}

function Field({ label, name, value, onChange, type = "text", required, testId }) {
    return (
        <label className="block">
            <span className="block text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-3">
                {label}
                {required && <span className="text-accent"> *</span>}
            </span>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                data-testid={testId}
                className="w-full bg-transparent border-b hairline focus:border-foreground outline-none py-3 font-mono-ui text-sm"
            />
        </label>
    );
}

function GuaranteeBand() {
    return (
        <section
            id="guarantee"
            className="bg-accent text-accent-foreground py-16 md:py-24 relative overflow-hidden"
            data-testid="guarantee-band"
        >
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6 items-center">
                <div className="col-span-12 md:col-span-2 flex md:justify-start">
                    <div className="w-20 h-20 md:w-24 md:h-24 border-2 border-accent-foreground grid place-items-center">
                        <ShieldCheck size={36} strokeWidth={1.4} />
                    </div>
                </div>
                <div className="col-span-12 md:col-span-7">
                    <div className="text-[11px] tracking-[0.28em] uppercase text-accent-foreground/70 mb-4 flex items-center gap-3">
                        <span className="w-8 h-px bg-accent-foreground/70" />
                        The Access Guarantee
                    </div>
                    <h2 className="font-editorial text-5xl md:text-7xl tracking-tighter leading-[0.9]">
                        A <span className="italic">93-point</span> increase — <span className="italic">guaranteed.</span>
                    </h2>
                    <p className="mt-6 max-w-xl text-sm md:text-base text-accent-foreground/85 leading-relaxed">
                        Enroll and follow our program. If your score doesn&rsquo;t climb by at
                        least <strong>93 points</strong>, we&rsquo;ll keep working — at no
                        additional cost — until it does. Put in writing. No small print.
                    </p>
                    <Link
                        to="/enroll"
                        data-testid="guarantee-cta"
                        className="mt-8 group inline-flex items-center gap-3 bg-background text-foreground px-6 py-4 text-[13px] tracking-[0.16em] uppercase hover:bg-foreground hover:text-background"
                    >
                        Get Started · $500 / year
                        <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </Link>
                </div>
                <div className="col-span-12 md:col-span-3 md:text-right">
                    <div className="font-editorial text-[96px] md:text-[128px] leading-[0.86] tracking-tighter">
                        +93
                    </div>
                    <div className="mt-1 font-mono-ui text-[11px] tracking-[0.22em] uppercase text-accent-foreground/70">
                        points · guaranteed
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function Landing() {
    return (
        <div className="bg-background text-foreground">
            <Nav />
            <HeroParallax />
            <EditorialMarquee />
            <GuaranteeBand />
            <ChaptersSection />
            <ProcessSection />
            <ResultsSection />
            <ConsultSection />
            <Footer />
        </div>
    );
}
